import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab
} from '@mui/material';
import {
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

/**
 * ERPProjectRatings Component (v2.0)
 * Displays employee project ratings synced from ERP system
 * Now shows individual project ratings instead of just aggregated averages
 * 
 * Props:
 * - employeeEmail: If provided, shows ratings for specific employee
 * - showSyncControls: Whether to show admin sync controls (default: false)
 * - compact: Show compact view (default: false)
 * - year: Optional year to filter ratings
 */
const ERPProjectRatings = ({ employeeEmail, showSyncControls = false, compact = false, year = null }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ratingData, setRatingData] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(year || new Date().getFullYear());
  const [tabValue, setTabValue] = useState(0);
  const [projectDetailDialog, setProjectDetailDialog] = useState({ open: false, project: null });

  useEffect(() => {
    if (employeeEmail) {
      fetchEmployeeRatings();
    } else {
      fetchStats();
    }
  }, [employeeEmail, selectedYear]);

  const fetchEmployeeRatings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}/erp-ratings/current-cycle/${encodeURIComponent(employeeEmail)}`,
        { 
          headers: getAuthHeaders(),
          params: { year: selectedYear }
        }
      );
      
      if (response.data.success && response.data.data) {
        setRatingData(response.data.data);
      } else {
        setRatingData(null);
      }
    } catch (err) {
      console.error('Error fetching ERP ratings:', err);
      setError('Failed to load ERP project ratings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/erp-ratings/stats`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (score) => {
    if (score >= 4.5) return '#4caf50'; // Green - Exceptional
    if (score >= 4.0) return '#8bc34a'; // Light green - Exceeds
    if (score >= 3.0) return '#ff9800'; // Orange - Meets
    if (score >= 2.0) return '#ff5722'; // Deep Orange - Needs Improvement
    return '#f44336'; // Red - Below
  };

  const getRatingGrade = (score) => {
    if (score >= 4.5) return 'Exceptional';
    if (score >= 4.0) return 'Exceeds Expectations';
    if (score >= 3.0) return 'Meets Expectations';
    if (score >= 2.0) return 'Needs Improvement';
    return 'Below Expectations';
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<StarIcon key={i} sx={{ color: '#ffc107', fontSize: 18 }} />);
      } else if (i === fullStars && hasHalf) {
        stars.push(<StarIcon key={i} sx={{ color: '#ffc107', fontSize: 18, opacity: 0.5 }} />);
      } else {
        stars.push(<StarBorderIcon key={i} sx={{ color: '#ccc', fontSize: 18 }} />);
      }
    }
    return stars;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Compact view for embedding in other pages
  if (compact && ratingData) {
    const compactProjectRatings = ratingData.projectRatings || [];
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2" color="text.secondary">
              ERP Project Ratings
            </Typography>
            <Chip
              size="small"
              label={getRatingGrade(ratingData.overallAverage)}
              sx={{ 
                bgcolor: getRatingColor(ratingData.overallAverage),
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h4" sx={{ color: getRatingColor(ratingData.overallAverage) }}>
              {ratingData.overallAverage?.toFixed(1) || 'N/A'}
            </Typography>
            <Box>
              <Box display="flex">{renderStars(ratingData.overallAverage || 0)}</Box>
              <Typography variant="caption" color="text.secondary">
                Based on {ratingData.totalPeerRatings || 0} peer ratings from {compactProjectRatings.length} projects
              </Typography>
            </Box>
          </Box>

          {/* Show recent projects in compact view */}
          {compactProjectRatings.length > 0 && (
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                Recent Projects:
              </Typography>
              {compactProjectRatings.slice(0, 3).map((project, idx) => (
                <Box key={idx} display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {project.projectName || project.projectNo}
                  </Typography>
                  <Chip
                    label={project.averageRating?.toFixed(1)}
                    size="small"
                    sx={{ 
                      bgcolor: getRatingColor(project.averageRating),
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: 45
                    }}
                  />
                </Box>
              ))}
              {compactProjectRatings.length > 3 && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  +{compactProjectRatings.length - 3} more projects
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Last synced: {formatDate(ratingData.syncTimestamp)}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // No ratings found
  if (!loading && !ratingData && employeeEmail) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No ERP project ratings found for this employee.
      </Alert>
    );
  }

  // Stats view for admin
  if (!employeeEmail && stats) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <AssessmentIcon color="primary" />
              <Typography variant="h6">ERP Project Ratings Sync</Typography>
            </Box>
            {showSyncControls && (
              <Tooltip title="Ratings are pushed from ERP system">
                <IconButton size="small">
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6} md={2.4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                <Typography variant="h4" color="primary">{stats.totalEmployees || 0}</Typography>
                <Typography variant="body2">Employees</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                <Typography variant="h4" color="success.main">{stats.totalProjects || 0}</Typography>
                <Typography variant="body2">Total Projects</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                <Typography variant="h4" color="warning.main">{stats.mappedRatings || 0}</Typography>
                <Typography variant="body2">Mapped</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                <Typography variant="h4" color="error.main">{stats.unmappedRatings || 0}</Typography>
                <Typography variant="body2">Unmapped</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fce4ec' }}>
                <Typography variant="h4" color="secondary">{stats.currentYearProjects || 0}</Typography>
                <Typography variant="body2">{new Date().getFullYear()} Projects</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Sync Version: {stats.syncVersion || 'N/A'}
            </Typography>
            {stats.lastSyncTimestamp && (
              <Typography variant="caption" color="text.secondary">
                Last sync: {new Date(stats.lastSyncTimestamp).toLocaleString()}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Full rating view
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!ratingData) {
    return <Alert severity="info">No rating data available.</Alert>;
  }

  const projectRatings = ratingData.projectRatings || [];

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6">ERP Project Ratings</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              select
              size="small"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ minWidth: 100 }}
            >
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return <MenuItem key={year} value={year}>{year}</MenuItem>;
              })}
            </TextField>
            <IconButton onClick={fetchEmployeeRatings} size="small">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Overall Score Card */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 2, 
            bgcolor: '#f5f5f5', 
            borderLeft: `4px solid ${getRatingColor(ratingData.overallAverage)}` 
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: getRatingColor(ratingData.overallAverage),
                    color: 'white'
                  }}
                >
                  <Typography variant="h4">{ratingData.overallAverage?.toFixed(1) || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {getRatingGrade(ratingData.overallAverage || 0)}
                  </Typography>
                  <Box display="flex">{renderStars(ratingData.overallAverage || 0)}</Box>
                  <Typography variant="caption" color="text.secondary">
                    Overall Average
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <WorkIcon color="action" />
                    <Box>
                      <Typography variant="h5">{projectRatings.length}</Typography>
                      <Typography variant="caption">Projects</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PeopleIcon color="action" />
                    <Box>
                      <Typography variant="h5">{ratingData.uniqueRatersCount || 0}</Typography>
                      <Typography variant="caption">Peers</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <StarIcon color="action" />
                    <Box>
                      <Typography variant="h5">{ratingData.totalPeerRatings || 0}</Typography>
                      <Typography variant="caption">Total Ratings</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs for different views */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label={`Project Ratings (${projectRatings.length})`} icon={<WorkIcon />} iconPosition="start" />
            <Tab label="Category Summary" icon={<TrendingUpIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tab 0: Project-wise Ratings */}
        {tabValue === 0 && (
          <Box>
            {projectRatings.length === 0 ? (
              <Alert severity="info">No project ratings found for the selected period.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Project</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Rating</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Grade</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Raters</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Date</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectRatings.map((project, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {project.projectNo}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {project.projectName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={project.averageRating?.toFixed(1)} 
                            size="small"
                            sx={{ 
                              bgcolor: getRatingColor(project.averageRating),
                              color: 'white',
                              fontWeight: 'bold',
                              minWidth: 50
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {getRatingGrade(project.averageRating)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                            <PeopleIcon fontSize="small" color="action" />
                            <Typography variant="body2">{project.uniqueRaters || 0}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {formatDate(project.ratingDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            onClick={() => setProjectDetailDialog({ open: true, project })}
                          >
                            <ExpandMoreIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Tab 1: Category Summary */}
        {tabValue === 1 && (
          <Box>
            {ratingData.categoryBreakdown && ratingData.categoryBreakdown.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Category</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Score</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Grade</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ratingData.categoryBreakdown.map((cat, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{cat.category}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={cat.average?.toFixed(1)} 
                            size="small"
                            sx={{ 
                              bgcolor: getRatingColor(cat.average),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {getRatingGrade(cat.average)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: 200 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(cat.average / 5) * 100}
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: getRatingColor(cat.average),
                                borderRadius: 4
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No category breakdown available.</Alert>
            )}

            {/* Parameter Details Accordion */}
            {ratingData.parameterBreakdown && ratingData.parameterBreakdown.length > 0 && (
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">Detailed Parameter Ratings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Parameter</TableCell>
                          <TableCell align="center">Average</TableCell>
                          <TableCell align="center">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ratingData.parameterBreakdown.map((param, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              {param.parameter?.replace(/_\d+$/, '').replace(/_/g, ' - ')}
                            </TableCell>
                            <TableCell align="center">
                              <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                {renderStars(param.average || 0)}
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                  {param.average?.toFixed(1)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">{param.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}

        {/* Sync Info */}
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {ratingData.filteredPeriod 
              ? `Showing: ${formatDate(ratingData.filteredPeriod.start)} - ${formatDate(ratingData.filteredPeriod.end)}`
              : `All available project ratings`
            }
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last synced: {formatDate(ratingData.syncTimestamp)}
          </Typography>
        </Box>
      </CardContent>

      {/* Project Detail Dialog */}
      <Dialog 
        open={projectDetailDialog.open} 
        onClose={() => setProjectDetailDialog({ open: false, project: null })}
        maxWidth="sm"
        fullWidth
      >
        {projectDetailDialog.project && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6">{projectDetailDialog.project.projectNo}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {projectDetailDialog.project.projectName}
                  </Typography>
                </Box>
                <Chip 
                  label={projectDetailDialog.project.averageRating?.toFixed(1)} 
                  sx={{ 
                    bgcolor: getRatingColor(projectDetailDialog.project.averageRating),
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    height: 40,
                    minWidth: 60
                  }}
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Rating Grade</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {getRatingGrade(projectDetailDialog.project.averageRating)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Peers Who Rated</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {projectDetailDialog.project.uniqueRaters || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Total Ratings</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {projectDetailDialog.project.totalRatings || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Rating Date</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatDate(projectDetailDialog.project.ratingDate)}
                  </Typography>
                </Grid>
                {projectDetailDialog.project.completionDate && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Completion Date</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatDate(projectDetailDialog.project.completionDate)}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Project Status</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {projectDetailDialog.project.projectStatus || 'Completed'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Parameter breakdown for this project */}
              {projectDetailDialog.project.parameterBreakdown && projectDetailDialog.project.parameterBreakdown.length > 0 && (
                <Box mt={3}>
                  <Typography variant="subtitle2" gutterBottom>Parameter Breakdown</Typography>
                  <Divider sx={{ mb: 2 }} />
                  {projectDetailDialog.project.parameterBreakdown.map((param, idx) => (
                    <Box key={idx} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        {param.parameter?.replace(/_\d+$/, '').replace(/_/g, ' - ')}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={(param.average / 5) * 100}
                          sx={{ 
                            width: 100, 
                            height: 6, 
                            borderRadius: 3,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: getRatingColor(param.average)
                            }
                          }}
                        />
                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 30 }}>
                          {param.average?.toFixed(1)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setProjectDetailDialog({ open: false, project: null })}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Card>
  );
};

export default ERPProjectRatings;
